import { Logger } from '@/helpers/log';
import { asSingular, capitalise } from './string';

const logger = new Logger('api:amplify:lib:utilities:types');

const isObject = (o) => typeof o === 'object'
  && o !== null
  && !Array.isArray(o);

function isTypeDef(o) {
  if (!isObject(o)) {
    return false;
  }
  const typeKeys = ['type', 'default', 'detail'];
  return o.types === undefined
    && Object.keys(o).every((k) => typeKeys.includes(k));
}
function isUnionDef(o) {
  if (!isObject(o)) {
    return false;
  }
  const unionKeys = ['types', 'default', 'detail'];
  return o.types !== undefined
    && Object.keys(o).every((k) => unionKeys.includes(k));
}
const isArrayDef = (o) => Array.isArray(o) && o.length === 1 && isTypeDef(o[0]);

const substitute = (d) => (o, i) => {
  const s = i === undefined ? d : d[i];
  if (s === undefined) { return o; }
  if (o === null) { return s; }
  return o;
};
const substituteFn = (d, f) => (o, i) => {
  if (i === undefined && o === null) {
    return d;
  }
  return f(o, i);
};

function mergeBuilders(builders, key, build, nested) {
  const buildKey = `build${capitalise(asSingular(key))}`;
  if (builders[buildKey] !== undefined) {
    throw TypeError(`Cannot override existing key ${buildKey}`);
  }
  builders[buildKey] = build; // eslint-disable-line no-param-reassign
  Object.entries(nested).forEach((([k, v]) => {
    if (builders[k] !== undefined) {
      throw TypeError(`Cannot override existing key ${k}`);
    }
    builders[k] = v; // eslint-disable-line no-param-reassign
  }));
}

const builder = (fields) => (partial) => Object.entries(partial || fields)
  .reduce((o, [k, v]) => {
    const p = typeof v === 'function' ? null : v;
    const b = fields[k];
    if (b === undefined) {
      logger.warn('Attempt to build undefined key', k);
      return o;
    }
    const value = (Array.isArray(p) && p.length)
      ? p.map(b)
      : b(p);
    return { ...o, [k]: value };
  }, {});

function scaffold(type) {
  const builders = {};
  const fields = Object.entries(type)
    .reduce((f, [k, v]) => {
      if (isTypeDef(v)) {
        return { ...f, [k]: substitute(v.default) };
      }
      let build;
      let nestedBuilders = {};
      if (isUnionDef(v)) {
        const { default: nested, types } = v;
        ({ build } = scaffold(nested));
        Object.entries(types).forEach(([n, t]) => {
          const { build: b } = scaffold(t);
          mergeBuilders(builders, n, b, {});
        });
      } else if (isObject(v)) {
        const nested = v;
        ({ builders: nestedBuilders, build } = scaffold(nested));
      } else if (isArrayDef(v)) {
        const { type: nested, default: d } = v[0];
        ({ builders: nestedBuilders, build } = scaffold(nested));
        build = substituteFn(d, build);
      } else {
        throw TypeError(`Untyped key { ${k}: ${v} }`);
      }
      mergeBuilders(builders, k, build, nestedBuilders);
      return { ...f, [k]: build };
    }, {});

  const build = builder(fields);
  return { builders, build };
}

export function getBuilders(type) {
  const { builders, build } = scaffold(type);
  return { build, ...builders };
}

export function getFiller(type) {
  const fill = (p, t) => {
    if (isUnionDef(t)) {
      return p === undefined ? fill(undefined, t.default) : p;
    }
    if (isTypeDef(t)) {
      return p === undefined ? t.default : p;
    }
    if (isArrayDef(t)) {
      const [def] = t;
      if (!Array.isArray(p)) {
        return def.default;
      }
      const filled = p.map((partial) => fill(partial, def.type));
      return filled;
    }
    if (isObject(t)) {
      const filled = Object.entries(t).reduce((f, [k, v]) => {
        const partial = p === undefined ? undefined : p[k];
        return { ...f, [k]: fill(partial, v) };
      }, {});
      return filled;
    }
    return p;
  };
  return (partial) => fill(partial || {}, type || {});
}
